import { React, useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../index.css"
import { img2, Logo } from "../assets";
import { navLinks } from "../constants";
import { socialMedia } from "../constants";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { motion } from "framer-motion";
import { MdAdd, MdFormatListBulletedAdd, MdHdrPlus, MdOutlineStarBorder, MdStar } from "react-icons/md";

gsap.registerPlugin(SplitText);

const Navbar= () => {
  const [active, setActive] = useState("Home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const navTogglerRef = useRef(null);
  const navBgRef = useRef([]);
  const navItemsRef = useRef(null);
  const navRef = useRef(null);
  const navContentRef = useRef(null);
  const tlRef = useRef(null);
  const splitLinksRef = useRef(null);
  const location = useLocation();

  // Set active based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const activeLink = navLinks.find(link => link.href === currentPath);
    if (activeLink) {
      setActive(activeLink.title);
    } else if (currentPath === "/") {
      setActive("Home");
    }
  }, [location]);

  // Navbar scroll animation
  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        if (window.scrollY > 50) {
          navRef.current.classList.add("scrolled");
        } else {
          navRef.current.classList.remove("scrolled");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    if (isMenuOpen) {
      handleCloseMenu();
    }
  }, [location.pathname]);

  // Initialize timeline
  useEffect(() => {
    if (navBgRef.current.length && navContentRef.current && navItemsRef.current) {
      // Set initial state
      gsap.set(navBgRef.current, { scaleY: 0 });
      gsap.set(navContentRef.current, { 
        display: "none",
        pointerEvents: "none" // Prevent interaction when hidden
      });
      
      tlRef.current = gsap.timeline({ 
        paused: true, 
        onComplete: () => { 
          setIsAnimating(false);
          // Enable pointer events when animation completes
          gsap.set(navContentRef.current, { pointerEvents: "auto" });
        },
        onReverseComplete: () => {
          gsap.set(navContentRef.current, { 
            display: "none",
            pointerEvents: "none" 
          });
          setIsAnimating(false);
        }
      });

      // Step 1: Show nav-content and animate background blocks
      tlRef.current.set(navContentRef.current, { display: "block" });
      tlRef.current.to(navBgRef.current, {
        duration: 0.8,
        scaleY: 1,
        stagger: 0.1,
        ease: "power3.inOut",
      });

      // Step 2: Animate nav items clipPath
      tlRef.current.to(navItemsRef.current, {
        duration: 0.7,
        clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)",
        ease: "power3.inOut",
      }, "-=0.5");
    }
  }, []);

  // Initialize SplitText
  useEffect(() => {
    if (navItemsRef.current) {
      const links = navItemsRef.current.querySelectorAll("a");
      if (links.length) {
        splitLinksRef.current = SplitText.create(links, { 
          type: "lines",
          linesClass: "line",
        });
      }
    }
  }, [isMenuOpen]); // Re-run when menu opens

  const animateLinksIn = () => {
    const linkBlocks = [
      ".nav-socials .line",
      ".nav-primary-links .line",
      ".nav-items-signup .line"
    ];
    
    linkBlocks.forEach((selector) => {
      gsap.fromTo(
        selector,
        { y: "100%", opacity: 0 },
        { 
          y: "0%",
          opacity: 1,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.05,
          delay: 0.2,
        }
      );  
    });
  };

  const handleCloseMenu = () => {
    if (isAnimating || !isMenuOpen) return;
    setIsAnimating(true);
    
    if (navTogglerRef.current) {
      navTogglerRef.current.classList.remove("open");
    }
    
    if (tlRef.current) {
      tlRef.current.reverse();
    }
    
    setIsMenuOpen(false);
  };

  const handleToggle = () => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (navTogglerRef.current) {
      navTogglerRef.current.classList.toggle("open");
    }

    if (!isMenuOpen && tlRef.current) {
      // Reset before playing
      gsap.set(navBgRef.current, { scaleY: 0 });
      gsap.set(navContentRef.current, { display: "none", pointerEvents: "none" });
      gsap.set(navItemsRef.current, { clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)" });
      
      tlRef.current.play();
      animateLinksIn();
    } else if (tlRef.current) {
      tlRef.current.reverse();
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (title, href) => {
    setActive(title);
    handleCloseMenu();
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (tlRef.current) {
        tlRef.current.kill();
      }
      if (splitLinksRef.current) {
        splitLinksRef.current.revert();
      }
    };
  }, []);

  return (
    <>
      <nav ref={navRef} className="fixed-nav ">
        <Link to="/" className="p-2">
          <img src={Logo} alt="logo" className="w-[138px] h-[36px]" />
        </Link>
        
        <button 
          className="nav-toggler"
          onClick={handleToggle}
          ref={navTogglerRef}
        >
          <span></span>
          <span></span>
        </button>

        {/* Fullscreen Menu Overlay */}
        <div className="nav-content drop-shadow-md" ref={navContentRef}>
          {/* Animated Background Blocks */}
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>

          {/* Menu Content */}
          <div className="nav-menu-content">
            <div 
              style={{ 
                backgroundImage: `url(${img2})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                zIndex: 0,
              }}
              className="nav-items" 
              ref={navItemsRef}
            >
              <div className="nav-items-col">
                <ul className="list-none nav-primary-links">
                  {navLinks.map((nav, index) => (
                    <li
                      key={nav.id || index}                      
                    >
                      <Link 
                        to={nav.href}
                        onClick={() => handleNavClick(nav.title, nav.href)}
                        className={` cursor-pointer overflow-hidden
                        ${active === nav.title 
                          ? "font-extrabold text-md text-[#660099]" 
                          : "font-medium text-sm"
                        } 
                        transition-colors duration-300
                      `}
                      >
                        <div className="flex flex-row gap-3 items-center">
                          <div className="flex flex-col gap-2 items-start">
                            {nav.title}
                            <span 
                              className={`border-b border-0.5 border-[#660099]
                                ${active === nav.title 
                                  ? "w-[40px]" 
                                  : "w-[40px]"
                                } 
                              `}
                              ></span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;